import type {
  Action,
  AggregateRating,
  InteractionCounter,
  ItemList,
  Offer,
  Organization,
  Person,
  Thing,
  VideoGallery,
  VideoObject,
  WithContext,
} from 'schema-dts';

export type SchemaVideoObject = VideoObject & {
  about?: Thing;
  creator?: Person;
  publisher: Organization;
  offers?: Offer;
  interactionStatistic?: InteractionCounter[];
  aggregateRating?: AggregateRating;
  potentialAction?: Action[];
  accessibilityFeature?: string[];
  contentTags?: string[];

  videoQuality?: string;
  bitrate?: string;
  license?: string;
  acquireLicensePage?: string;
};

export function isItemList(
  schema: ItemList | VideoGallery
): schema is ItemList {
  return schema['@type'] === 'ItemList';
}

export function isVideoGallery(
  schema: ItemList | VideoGallery
): schema is VideoGallery {
  return schema['@type'] === 'VideoGallery';
}

// Base video item interface - defines the minimum required structure
export interface BaseVideoItem {
  id: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  mediumImageUrl?: string;
  largeImageUrl?: string;
  previewUrl?: string;
  detailsUrl?: string;
  dateAdded?: string;
  dateModified?: string;
  duration?: number;
  maxResolution?: number;
  aspectRatio?: number;
  fileSize?: number;
  bitrate?: number;
  rating?: number;
  ratingCount?: number;
  viewCount?: number;
  downloadCount?: number;
  language?: string;
  hasClosedCaptions?: boolean;
  tags?: string[];
  categories?: Array<{
    name: string;
  }>;
  contributor?: {
    username: string;
    profileImageUrl?: string;
  };
}

// Video container interface - defines how video items are wrapped
export interface VideoContainer<T extends BaseVideoItem = BaseVideoItem> {
  stockItem: T;
}

export interface VideoSchemaGeneratorProps<
  T extends VideoContainer<BaseVideoItem>,
> {
  heading: string;
  description?: string;
  videos: T[];
  pageUrl?: string;
  contentMode?: 'search' | 'collection' | 'mixed';
  category?: string;
  searchTerm?: string;
  totalResults?: number;
  currentPage?: number;
  itemsPerPage?: number;
}

const durationCache = new Map<number, string>();

const videoObjectCache = new Map<string, unknown>();

/**
 * Convert relative URL to absolute URL
 * Ensures all URLs in schema.org markup are properly formatted for SEO
 */
const toAbsoluteUrl = (url: string): string => {
  if (!url) return '';

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  const baseUrl = 'https://www.storyblocks.com';
  return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
};

/**
 * Optimized duration conversion with caching
 */
const convertToIsoDuration = (seconds: number): string => {
  if (durationCache.has(seconds)) {
    return durationCache.get(seconds) ?? 'PT0S';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  let isoDuration = 'PT';
  if (hours > 0) isoDuration += `${hours}H`;
  if (minutes > 0) isoDuration += `${minutes}M`;
  if (secs > 0) isoDuration += `${secs}S`;

  durationCache.set(seconds, isoDuration);
  return isoDuration;
};

/**
 * Generate video format information
 */
const getVideoFormat = (stockItem: BaseVideoItem): Record<string, unknown> => {
  const format: Record<string, unknown> = {
    encodingFormat: 'video/mp4',
  };

  if (stockItem.maxResolution) {
    format.videoQuality = `${stockItem.maxResolution}p`;
    format.height = `${stockItem.maxResolution}px`;
    if (stockItem.aspectRatio) {
      const ratio = parseFloat(stockItem.aspectRatio.toString());
      format.width = `${Math.round(stockItem.maxResolution * ratio)}px`;
    } else {
      format.width = `${Math.round(stockItem.maxResolution * (16 / 9))}px`;
    }
  }

  if (stockItem.fileSize) {
    format.contentSize = `${stockItem.fileSize}MB`;
  }

  if (stockItem.bitrate) {
    format.bitrate = `${stockItem.bitrate}kbps`;
  }

  return format;
};

/**
 * Generate pricing and licensing information
 */
const getPricingInfo = (stockItem: BaseVideoItem): Record<string, unknown> => {
  const pricing: Record<string, unknown> = {
    license: 'https://www.storyblocks.com/pricing',
    acquireLicensePage: stockItem.detailsUrl
      ? toAbsoluteUrl(stockItem.detailsUrl)
      : undefined,
    isAccessibleForFree: false, // Storyblocks requires subscription
    offers: {
      '@type': 'Offer',
      category: 'Subscription',
      businessFunction: {
        '@type': 'BusinessFunction',
        '@id': 'http://purl.org/goodrelations/v1#LeaseOut',
      },
      seller: {
        '@type': 'Organization',
        name: 'Storyblocks',
        url: 'https://www.storyblocks.com',
      },
      url: 'https://www.storyblocks.com/pricing',
      description: 'Individual license, higher quality stock content and more.',
    },
  };

  return pricing;
};

/**
 * Generate interaction statistics
 */
const getInteractionStatistics = (
  stockItem: BaseVideoItem
): Record<string, any> => {
  const stats: Record<string, any> = {};

  if (stockItem.viewCount) {
    const watchInteraction = {
      '@type': 'InteractionCounter' as const,
      '@id': 'https://schema.org/WatchAction',
      interactionType: {
        '@type': 'Action',
        '@id': 'https://schema.org/WatchAction',
      },
      userInteractionCount: Number(stockItem.viewCount),
    };
    stats.interactionStatistic = [watchInteraction];
  }

  if (stockItem.downloadCount) {
    const downloadInteraction = {
      '@type': 'InteractionCounter' as const,
      '@id': 'https://schema.org/DownloadAction',
      interactionType: {
        '@type': 'Action',
        '@id': 'https://schema.org/DownloadAction',
      },
      userInteractionCount: Number(stockItem.downloadCount),
    };

    if (!stats.interactionStatistic) {
      stats.interactionStatistic = [downloadInteraction];
    } else {
      stats.interactionStatistic.push(downloadInteraction);
    }
  }

  if (stockItem.rating && stockItem.ratingCount) {
    const rating = {
      '@type': 'AggregateRating' as const,
      ratingValue: Number(stockItem.rating),
      ratingCount: Number(stockItem.ratingCount),
      bestRating: 5,
      worstRating: 1,
    };
    stats.aggregateRating = rating;
  }

  return stats;
};

/**
 * Enhanced video object generation with comprehensive SEO properties
 */
const createVideoObject = <T extends BaseVideoItem>(
  videoItem: T,
  index: number
): any => {
  const dateModified = videoItem.dateModified || videoItem.dateAdded;
  const cacheKey = `${videoItem.id}-${dateModified}`;

  if (videoObjectCache.has(cacheKey)) {
    return videoObjectCache.get(cacheKey);
  }

  const fallbackTitle = `Video ${index + 1}`;
  const videoObject: any = {
    '@type': 'VideoObject',
    '@id': `#video-${videoItem.id || index}`,
    name: videoItem.title || fallbackTitle,
    description:
      videoItem.description ||
      `${
        videoItem.title || fallbackTitle
      } - Royalty-free stock video from Storyblocks`,
    thumbnailUrl:
      videoItem.thumbnailUrl ||
      videoItem.mediumImageUrl ||
      videoItem.largeImageUrl,
    uploadDate: videoItem.dateAdded || new Date().toISOString(),
    url: videoItem.detailsUrl ? toAbsoluteUrl(videoItem.detailsUrl) : undefined,
    embedUrl: videoItem.previewUrl,
    contentUrl: videoItem.previewUrl,

    publisher: {
      '@type': 'Organization',
      name: 'Storyblocks',
      url: 'https://www.storyblocks.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F8977452afd404719a1cfac9521c44586',
        width: '200px',
        height: '50px',
      },
      sameAs: [
        'https://www.facebook.com/storyblocks/',
        'https://twitter.com/StoryblocksCo',
        'https://www.linkedin.com/company/storyblocks/',
        'https://www.youtube.com/channel/UCrxwwZTQOPCTUVlHsjNU7Fw',
        'https://www.instagram.com/StoryblocksCo/',
      ],
    },
  };

  if (videoItem.duration) {
    videoObject.duration = convertToIsoDuration(videoItem.duration);
  }

  if (videoItem.categories?.length) {
    videoObject.keywords = videoItem.categories
      .map((cat) => cat.name)
      .join(', ');
    videoObject.genre = videoItem.categories.map((cat) => cat.name);
    videoObject.about = {
      '@type': 'Thing',
      name: videoItem.categories.map((cat) => cat.name).join(', '),
      url: toAbsoluteUrl(
        `/video/search/${encodeURIComponent(
          videoItem.categories.map((cat) => cat.name).join(', ')
        )}`
      ),
    };
  }

  if (videoItem.contributor?.username) {
    videoObject.creator = {
      '@type': 'Person',
      name: videoItem.contributor.username,
      url: toAbsoluteUrl(`/video/portfolio/${videoItem.contributor.username}`),
    };

    if (videoItem.contributor.profileImageUrl) {
      videoObject.creator.image = videoItem.contributor.profileImageUrl;
    }
  }

  const formatInfo = getVideoFormat(videoItem);
  Object.assign(videoObject, formatInfo);

  const pricingInfo = getPricingInfo(videoItem);
  Object.assign(videoObject, pricingInfo);

  const statsInfo = getInteractionStatistics(videoItem);
  Object.assign(videoObject, statsInfo);

  if (videoItem.tags?.length) {
    videoObject.contentTags = videoItem.tags;
  }

  if (videoItem.language) {
    videoObject.inLanguage = videoItem.language;
  }

  videoObject.potentialAction = [
    {
      '@type': 'WatchAction',
      target: videoItem.previewUrl,
      name: 'Preview Video',
    },
    {
      '@type': 'DownloadAction',
      target: videoItem.detailsUrl
        ? toAbsoluteUrl(videoItem.detailsUrl)
        : undefined,
      name: 'Download Video',
    },
    {
      '@type': 'ShareAction',
      target: videoItem.detailsUrl
        ? toAbsoluteUrl(videoItem.detailsUrl)
        : undefined,
      name: 'Share Video',
    },
  ].filter((action) => action.target); // Remove actions with undefined targets

  if (videoItem.hasClosedCaptions) {
    videoObject.accessibilityFeature = ['closedCaptions'];
  }

  videoObjectCache.set(cacheKey, videoObject);

  return videoObject;
};

/**
 * Generates optimized JSON-LD schema for video collections
 * Enhanced for SEO with comprehensive schema.org compliance
 */
export const generateVideoCollectionSchema = <
  T extends VideoContainer<BaseVideoItem>,
>({
  heading,
  description,
  videos,
  pageUrl,
  contentMode,
  category,
  searchTerm,
  totalResults,
  currentPage,
  itemsPerPage,
}: VideoSchemaGeneratorProps<T>): string => {
  if (!videos?.length) {
    return '';
  }

  const validVideos = videos.filter((video) => video?.stockItem?.id);
  if (validVideos.length === 0) {
    return '';
  }

  const generateDescription = (): string => {
    if (description) return description;

    const count = totalResults || videos.length;
    const baseDescription = `Browse ${count.toLocaleString()} royalty-free video${
      count === 1 ? '' : 's'
    }`;

    if (searchTerm) {
      return `${baseDescription} for "${searchTerm}" on Storyblocks. High-quality stock footage for your creative projects.`;
    }

    if (category) {
      return `${baseDescription} in the ${category} category on Storyblocks. Professional stock video content.`;
    }

    return `${baseDescription} on Storyblocks. Premium stock video library for creators.`;
  };

  const videoObjects = validVideos.map((video, index) =>
    createVideoObject(video.stockItem, index)
  );

  const schema = {
    '@context': 'https://schema.org',
    '@type': contentMode === 'collection' ? 'VideoGallery' : 'ItemList',
    name: heading,
    description: generateDescription(),
    numberOfItems: totalResults || validVideos.length,

    publisher: {
      '@type': 'Organization',
      name: 'Storyblocks',
      url: 'https://www.storyblocks.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F8977452afd404719a1cfac9521c44586',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        url: 'https://www.storyblocks.com/support',
      },
    },

    itemListElement: videoObjects.map((video, index) => ({
      '@type': 'ListItem',
      position:
        (currentPage
          ? (currentPage - 1) * (itemsPerPage || validVideos.length)
          : 0) +
        index +
        1,
      item: video,
    })),
  } as WithContext<ItemList | VideoGallery>;

  if (pageUrl) {
    schema.url = pageUrl;
  }

  if (currentPage && itemsPerPage && totalResults) {
    const totalPages = Math.ceil(totalResults / itemsPerPage);

    if (totalPages > 1) {
      (schema as any).mainEntity = {
        '@type': 'CollectionPage',
        isPartOf: {
          '@type': 'ItemList',
          numberOfItems: totalResults,
        },
      };
    }
  }

  if (contentMode === 'search' && (category || searchTerm)) {
    (schema as any).about = {
      '@type': 'Thing',
      name: searchTerm || category,
      description: `Professional video content related to ${
        searchTerm || category
      }`,
      sameAs: category
        ? toAbsoluteUrl(`/video/search/${encodeURIComponent(category)}`)
        : undefined,
    };
  }

  if (category) {
    (schema as any).breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://www.storyblocks.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Videos',
          item: 'https://www.storyblocks.com/video',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: category,
          item: toAbsoluteUrl(`/video/search/${encodeURIComponent(category)}`),
        },
      ],
    };
  }

  (schema as any).isPartOf = {
    '@type': 'WebSite',
    name: 'Storyblocks',
    url: 'https://www.storyblocks.com',
    potentialAction: {
      '@type': 'SearchAction',
      target:
        'https://www.storyblocks.com/video/search?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return JSON.stringify(schema, null, 0);
};

/**
 * Lightweight schema for loading states or minimal SEO needs
 */
export const generateBasicVideoListSchema = (
  heading: string,
  videoCount: number,
  pageUrl?: string
): string => {
  const schema: WithContext<ItemList> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: heading,
    numberOfItems: videoCount,
    description: `Browse ${videoCount.toLocaleString()} royalty-free video${
      videoCount === 1 ? '' : 's'
    } on Storyblocks`,
    publisher: {
      '@type': 'Organization',
      name: 'Storyblocks',
      url: 'https://www.storyblocks.com',
    },
  } as WithContext<ItemList>;

  if (pageUrl) {
    (schema as any).url = pageUrl;
  }

  return JSON.stringify(schema, null, 0);
};

/**
 * Clear caches when needed (useful for memory management)
 */
export const clearSchemaCache = (): void => {
  videoObjectCache.clear();
  durationCache.clear();
};
